/**
 * DOM Utility functions to replace jQuery functionality
 */
export class DOMUtils {
  /**
   * Get multiple elements by ID in one call
   * @param {...string} ids - Element IDs to fetch
   * @returns {Array} Array of elements in the same order as IDs
   */
  static getElements(...ids) {
    return ids.map((id) => document.getElementById(id));
  }

  /**
   * Get multiple elements by ID as an object
   * @param {Object} mapping - Object with key: elementId pairs
   * @returns {Object} Object with key: element pairs
   */
  static getElementsAsObject(mapping) {
    const result = {};
    for (const [key, id] of Object.entries(mapping)) {
      result[key] = document.getElementById(id);
    }
    return result;
  }

  /**
   * Execute callback when DOM is ready
   * @param {Function} callback
   */
  static ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  /**
   * Get element by ID
   * @param {string} id
   * @returns {Element|null}
   */
  static getElementById(id) {
    return document.getElementById(id);
  }

  /**
   * Query selector
   * @param {string} selector
   * @returns {Element|null}
   */
  static querySelector(selector) {
    return document.querySelector(selector);
  }

  /**
   * Query all selectors
   * @param {string} selector
   * @returns {NodeList}
   */
  static querySelectorAll(selector) {
    return document.querySelectorAll(selector);
  }

  /**
   * Get element value
   * @param {string} id
   * @returns {string}
   */
  static getValue(id) {
    const element = document.getElementById(id);
    return element ? element.value : '';
  }

  /**
   * Set element value
   * @param {string} id
   * @param {string} value
   */
  static setValue(id, value) {
    const element = document.getElementById(id);
    if (element) {
      element.value = value;
    }
  }

  /**
   * Get element text content
   * @param {string} id
   * @returns {string}
   */
  static getText(id) {
    const element = document.getElementById(id);
    return element ? element.textContent : '';
  }

  /**
   * Set element text content
   * @param {string} id
   * @param {string} text
   */
  static setText(id, text) {
    const element = document.getElementById(id);
    if (element) {
      element.textContent = text;
    }
  }

  /**
   * Focus on element
   * @param {string} id
   */
  static focus(id) {
    const element = document.getElementById(id);
    if (element) {
      element.focus();
    }
  }

  /**
   * Add event listener
   * @param {string|Element} target
   * @param {string} event
   * @param {Function} callback
   * @param {Object} options
   */
  static addEventListener(target, event, callback, options = {}) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    if (element) {
      element.addEventListener(event, callback, options);
    }
  }

  /**
   * Remove class
   * @param {string|Element} target
   * @param {string} className
   */
  static removeClass(target, className) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    if (element) {
      element.classList.remove(className);
    }
  }

  /**
   * Add class
   * @param {string|Element} target
   * @param {string} className
   */
  static addClass(target, className) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    if (element) {
      element.classList.add(className);
    }
  }

  /**
   * Toggle class
   * @param {string|Element} target
   * @param {string} className
   */
  static toggleClass(target, className) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    if (element) {
      element.classList.toggle(className);
    }
  }

  /**
   * Check if element has class
   * @param {string|Element} target
   * @param {string} className
   * @returns {boolean}
   */
  static hasClass(target, className) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    return element ? element.classList.contains(className) : false;
  }

  /**
   * Show element
   * @param {string|Element} target
   */
  static show(target) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    if (element) {
      element.style.display = '';
    }
  }

  /**
   * Hide element
   * @param {string|Element} target
   */
  static hide(target) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    if (element) {
      element.style.display = 'none';
    }
  }

  /**
   * Set or get attribute
   * @param {string|Element} target
   * @param {string} name
   * @param {string} value
   * @returns {string|void}
   */
  static attr(target, name, value = null) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    if (!element) {
      return;
    }

    if (value === null) {
      return element.getAttribute(name);
    } else {
      element.setAttribute(name, value);
    }
  }

  /**
   * Remove attribute
   * @param {string|Element} target
   * @param {string} name
   */
  static removeAttr(target, name) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    if (element) {
      element.removeAttribute(name);
    }
  }

  /**
   * Check if checkbox is checked
   * @param {string|Element} target
   * @returns {boolean}
   */
  static isChecked(target) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    return element ? element.checked : false;
  }

  /**
   * Set checkbox checked state
   * @param {string|Element} target
   * @param {boolean} checked
   */
  static setChecked(target, checked) {
    const element = typeof target === 'string' ? document.getElementById(target) : target;
    if (element) {
      element.checked = checked;
    }
  }

  /**
   * Create element
   * @param {string} tagName
   * @param {Object} attributes
   * @param {string} textContent
   * @returns {Element}
   */
  static createElement(tagName, attributes = {}, textContent = '') {
    const element = document.createElement(tagName);

    Object.keys(attributes).forEach((key) => {
      element.setAttribute(key, attributes[key]);
    });

    if (textContent) {
      element.textContent = textContent;
    }

    return element;
  }

  /**
   * Append element
   * @param {string|Element} parent
   * @param {Element} child
   */
  static append(parent, child) {
    const parentElement = typeof parent === 'string' ? document.getElementById(parent) : parent;
    if (parentElement) {
      parentElement.appendChild(child);
    }
  }
}
